<template>
  <div
    v-if="thumbUrls.length || showPlaceholderSkeleton"
    class="image-thumb-viewer"
    :class="['is-' + variant, { 'is-inline-flex': variant === 'compact' }]"
  >
    <!-- 缩略图未就绪时：仅方框内转圈，不用整块遮罩 -->
    <template v-if="showPlaceholderSkeleton">
      <div
        v-for="n in thumbLoadingCount"
        :key="'ph-' + n"
        class="image-thumb-viewer__item image-thumb-viewer__item--placeholder"
      >
        <i class="el-icon-loading image-thumb-viewer__spinner" />
      </div>
    </template>
    <template v-else>
      <div
        v-for="(url, idx) in visibleUrls"
        :key="idx"
        class="image-thumb-viewer__item"
        @click="openPreview(idx)"
      >
        <img :src="url" :alt="imgAlt">
        <!-- 限幅展示时角标提示总数，避免用户以为只有这一张 -->
        <span
          v-if="hiddenCount && idx === visibleUrls.length - 1"
          class="image-thumb-viewer__more"
        >共{{ imageTotal }}张</span>
      </div>
      <ImagePreviewDialog
        :visible.sync="previewVisible"
        :image-url="previewUrl"
        :image-urls="previewUrls"
        :loading="previewLoading"
        :title="previewTitle"
        :width="previewWidth"
      />
    </template>
  </div>
</template>

<script>
import axios from 'axios';
import { parseImageFileIds, uniqueFileIds } from './imageFileIdsUtil';
import ImagePreviewDialog from './ImagePreviewDialog.vue';

/**
 * 只读缩略图 + 点击预览。
 * - 传入 urls：直接使用（如已解析的 data URL）
 * - 仅传入 fileIds：请求 /common/image 拉取 base64
 * 若同时传入且 urls 有有效项，优先使用 urls（避免重复请求）
 * 配 maxVisible 限幅时只预加载前几张，其余等点开预览再拉，列表页不会被图片请求拖慢
 */
export default {
  name: 'ImageThumbViewer',
  components: { ImagePreviewDialog },
  props: {
    urls: { type: Array, default: () => [] },
    fileIds: { type: [String, Array], default: null },
    variant: {
      type: String,
      default: 'default',
      validator: v => ['default', 'compact'].includes(v)
    },
    previewTitle: { type: String, default: '图片预览' },
    /** 处置单等小图场景可用 640px */
    previewWidth: { type: String, default: '600px' },
    imgAlt: { type: String, default: '' },
    /** 外部拉取缩略图期间：在固定数量方框内显示 loading（与 urls 二选一展示） */
    thumbLoading: { type: Boolean, default: false },
    /** 占位方框数量，应与待加载张数一致 */
    thumbLoadingCount: { type: Number, default: 1 },
    /** 最多渲染几张缩略图（0 为不限）；超出部分不渲染，用角标提示总数 */
    maxVisible: { type: Number, default: 0 },
    /** 点击缩略图时预览全部图片（默认只预览点击的那一张） */
    previewAll: { type: Boolean, default: false }
  },
  data () {
    return {
      /** fileId -> data URL，按 id 缓存以便限幅展示时能复用同一份数据 */
      loadedMap: {},
      loading: false,
      previewVisible: false,
      previewUrl: '',
      previewUrls: [],
      previewLoading: false
    };
  },
  computed: {
    directUrls () {
      return (this.urls || []).filter(u => u);
    },
    fileIdList () {
      return parseImageFileIds(this.fileIds);
    },
    /** 需要预加载的 id：限幅时只取前几张，其余留给预览时按需拉取 */
    preloadFileIds () {
      return this.maxVisible > 0 ? this.fileIdList.slice(0, this.maxVisible) : this.fileIdList;
    },
    thumbUrls () {
      if (this.directUrls.length) return this.directUrls;
      return this.toUrls(this.preloadFileIds);
    },
    /** 预览弹窗用的完整图片列表 */
    allUrls () {
      if (this.directUrls.length) return this.directUrls;
      return this.toUrls(this.fileIdList);
    },
    imageTotal () {
      return this.directUrls.length || this.fileIdList.length;
    },
    visibleUrls () {
      return this.maxVisible > 0 ? this.thumbUrls.slice(0, this.maxVisible) : this.thumbUrls;
    },
    hiddenCount () {
      return this.imageTotal - this.visibleUrls.length;
    },
    showPlaceholderSkeleton () {
      return this.thumbLoading && this.thumbUrls.length === 0 && this.thumbLoadingCount > 0;
    }
  },
  watch: {
    urls: {
      deep: true,
      handler () {
        if (!this.directUrls.length) this.maybeLoadFromIds();
      }
    },
    fileIds: {
      immediate: true,
      handler () {
        if (!this.directUrls.length) this.maybeLoadFromIds();
      }
    }
  },
  methods: {
    toUrls (ids) {
      return ids.map(id => this.loadedMap[id]).filter(Boolean);
    },
    maybeLoadFromIds () {
      if (this.directUrls.length) return;
      this.loadedMap = {};
      this.loadFileIds(this.preloadFileIds);
    },
    /** 按需拉取尚未缓存的图片，失败的单张跳过，不影响其余图片展示 */
    loadFileIds (ids) {
      const pending = uniqueFileIds(ids.filter(id => !this.loadedMap[id]));
      if (!pending.length) return Promise.resolve();
      this.loading = true;
      const imageApi = this.$config.ctx.iffApi + '/common/image';
      const headers = {
        [this.$config.serverHeaderTokenName]: this.$HMCookies.get(this.$config.serverHeaderTokenName)
      };
      const fetched = {};
      return Promise.all(
        pending.map(fileId =>
          axios.get(`${imageApi}/${fileId}`, { headers })
            .then(res => {
              if (res.data && res.data.data) fetched[fileId] = 'data:image/png;base64,' + res.data.data;
            })
            .catch(() => {})
        )
      )
        .then(() => {
          this.loadedMap = Object.assign({}, this.loadedMap, fetched);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    openPreview (idx) {
      if (!this.previewAll) {
        this.previewUrls = [];
        this.previewUrl = this.thumbUrls[idx];
        this.previewVisible = true;
        return;
      }
      this.previewUrl = '';
      this.previewUrls = [];
      this.previewLoading = true;
      this.previewVisible = true;
      // 弹窗先开再补图，避免点击后长时间无反馈
      this.loadFileIds(this.fileIdList).then(() => {
        this.previewUrls = this.allUrls;
      }).finally(() => {
        this.previewLoading = false;
      });
    }
  }
};
</script>

<style lang="scss" scoped>
.image-thumb-viewer {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;

  &.is-inline-flex {
    flex: 1;
    min-width: 0;
    gap: 8px;
  }

  &.is-default .image-thumb-viewer__item {
    width: 100px;
    height: 100px;
    border-radius: 6px;
  }

  &.is-compact .image-thumb-viewer__item {
    width: 56px;
    height: 56px;
    border-radius: 4px;
  }
}

.image-thumb-viewer__item {
  position: relative;
  overflow: hidden;
  border: 1px solid #ebeef5;
  cursor: pointer;
  transition: border-color 0.2s, box-shadow 0.2s;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }

  &:hover {
    border-color: #409eff;
    box-shadow: 0 2px 8px rgba(64, 158, 255, 0.2);
  }

  &--placeholder {
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: default;
    background: #f5f7fa;
    &:hover {
      border-color: #ebeef5;
      box-shadow: none;
    }
  }
}

.image-thumb-viewer__more {
  position: absolute;
  right: 0;
  bottom: 0;
  padding: 0 4px;
  font-size: 10px;
  line-height: 15px;
  color: #fff;
  background: rgba(0, 0, 0, 0.6);
}

.image-thumb-viewer__spinner {
  font-size: 22px;
  color: #409eff;
}
</style>
